import moment from 'moment'
import { MethodologyEnum, type PlanPosItem } from '~/protos/gen/stcs_comp_plan_cfg.entity'
import { StcsDisplayValueTypeEnum } from '~/protos/gen/stcs_enums.entity.ts'
import { useCtxStore } from '~/stores/ctx.store.ts'
import { createPlanningViewStore } from '~/stores/planningview.store'
import { createValidInputManualMap } from './unified-edit-api'

export function createManualChangePosEntry(row: PlanningRowData, isStatusUpdate = false): PlanPosItem {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState

  // Find direct child with rowClass='selected-method-row' by checking parent row_id
  // Use second-to-last element in path_level to identify parent (works with any filter state)
  const BaseRow = planningStore.selectedPlanState?.bsList.find((r) => {
    if (r.rowClass !== 'selected-method-row') return false
    const parentRowId = r.path_level[r.path_level.length - 2]
    return parentRowId === row.row_id
  }) as PlanningRowData

  const inputMap = createValidInputManualMap(BaseRow)

  // Get base amount key e.g. '2023-05' for May 2023
  const basAmountKey = moment(selectedPlan?.fpna_begin_date_string, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')

  // Extract base amount value from inputMap using basAmountKey
  const baseAmountValue = inputMap[basAmountKey]?.manual_number_input || 0

  // Remove the base amount key from inputMap so it doesn't get passed to pos_input_map
  if (inputMap[basAmountKey] !== undefined) {
    delete inputMap[basAmountKey]
  }

  // Get pos_res_plan_map from cache only when isStatusUpdate is true
  let pos_res_plan_map = {}
  if (isStatusUpdate && selectedPlan) {
    const parentRowId = row.path_level[row.path_level.length - 2]
    pos_res_plan_map = planningStore.planConfigCache[selectedPlan.id || '']?.stcs_comp_plan_cfg?.bs_map?.[parentRowId]?.pos_map?.[row.row_id]?.manual_dec_input_in_change?.pos_res_plan_map || {}
  }

  return {
    pos_logic_type: MethodologyEnum.MANUAL_DEC_INPUT_IN_CHANGE,
    fstmt_type: row.fstmt_type, // TODO @Sameer: This is required, please implement accordingly if not done
    display_value_type: StcsDisplayValueTypeEnum.SDVTE_CURRENCY,
    manual_dec_input_in_change: {
      pos_input_map: inputMap,
      pos_res_plan_map: pos_res_plan_map, // Populated from cache when isStatusUpdate is true
      pos_input_base_amount: baseAmountValue,
    },
    is_struct_item: false, // Handle by the backend only
    pos_index: 0,
    status: undefined, // Handle by the backend only
  }
}

export const handleManualChangePositions = (positionKey: string, planPosition: PlanPosItem) => {
  const selectedMethodRowKey = `${positionKey}_${getLevelIdForMethod(MethodologyEnum.MANUAL_DEC_INPUT_IN_CHANGE)}`

  // planning store
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const selectedPlan = planningStore.selectedPlanState
  if (!selectedPlan) return

  // values map from plan store
  const valuesMap = selectedPlan.valuesMapBs || {}

  // Extract the base amount and input map from the plan position
  const baseAmount = planPosition.manual_dec_input_in_change?.pos_input_base_amount || 0

  const inputMap = Object.fromEntries(Object.entries(planPosition.manual_dec_input_in_change?.pos_input_map || {}).map(([date, { manual_number_input }]) => [date, manual_number_input]))
  // Sort the input map by date keys to ensure chronological order
  const sortedInputEntries = Object.entries(inputMap).sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
  const basAmountKey = moment(selectedPlan?.fpna_begin_date_string, 'YYYY-MM').subtract(1, 'months').format('YYYY-MM')

  valuesMap[selectedMethodRowKey] = {
    values_map: {
      [basAmountKey]: baseAmount,
      ...Object.fromEntries(sortedInputEntries),
    },
  }

  selectedPlan.valuesMapBs = valuesMap
}
